"use client";

import { useMemo, useState } from "react";
import AreaCodeRow from "@/components/AreaCodeRow";
import NoResults from "@/components/NoResults";
import PrintableButton from "@/components/PrintableButton";
import { compactSearchInput } from "@/lib/areaCodeSearchCore";
import type { AreaCode } from "@/types/areaCode";

type RegionAreaCodeTableProps = {
  records: AreaCode[];
};

function groupByRegion(records: AreaCode[]) {
  const groups = new Map<string, AreaCode[]>();

  records.forEach((areaCode) => {
    const existing = groups.get(areaCode.region) ?? [];
    existing.push(areaCode);
    groups.set(areaCode.region, existing);
  });

  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([region, areaCodes]) => ({
      region,
      areaCodes: areaCodes.sort((a, b) => a.code.localeCompare(b.code)),
    }));
}

export default function RegionAreaCodeTable({ records }: RegionAreaCodeTableProps) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const normalized = compactSearchInput(query);
    if (!normalized) {
      return records;
    }

    return records.filter((areaCode) => {
      const haystack = compactSearchInput(
        [areaCode.code, areaCode.region, areaCode.regionAbbr, areaCode.country, ...areaCode.cities].join(" "),
      );
      return haystack.includes(normalized);
    });
  }, [query, records]);

  const groups = useMemo(() => groupByRegion(filtered), [filtered]);

  return (
    <section className="directory-section" aria-labelledby="by-region-title">
      <div className="directory-heading">
        <h2 id="by-region-title">By State, Province, and Territory</h2>
        <PrintableButton />
      </div>
      <div className="print:hidden mb-4 max-w-sm">
        <label className="mb-2 block text-sm font-semibold text-ink" htmlFor="search-by-city">
          Search by City
        </label>
        <input
          className="directory-input"
          id="search-by-city"
          placeholder='Enter "city, state"'
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      {groups.length > 0 ? (
        <div className="directory-table-wrap">
          <table className="directory-table">
            <caption className="sr-only">Area codes listed by state, province, and territory</caption>
            <thead>
              <tr>
                <th scope="col">State / Province / Territory</th>
                <th scope="col">Area Code</th>
                <th scope="col">Location</th>
                <th scope="col">Current Time</th>
              </tr>
            </thead>
            <tbody>
              {groups.flatMap((group) =>
                group.areaCodes.map((areaCode, index) => (
                  <AreaCodeRow
                    areaCode={areaCode}
                    key={`${group.region}-${areaCode.code}`}
                    regionRowSpan={group.areaCodes.length}
                    showRegionCell={index === 0}
                  />
                )),
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <NoResults message="No city, state, province, or territory matches that search." />
      )}
    </section>
  );
}
